import React, { useEffect, useState, useCallback } from 'react';
import { Container, Table, Badge, Button, Modal, Form, InputGroup, Dropdown } from 'react-bootstrap';
import { forumApi } from '../../api/forum';
import HrnbCard from '../../components/controls/HrnbCard';
import HrnbAlert from '../../components/controls/HrnbAlert';
import HrnbLoader from '../../components/controls/HrnbLoader';
import AdminPagination from '../components/pagination/AdminPagination';
import './AdminForumComments.css';

const AdminForumComments = () => {
    const [comments, setComments] = useState([]);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Batch-выбор
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [batchLoading, setBatchLoading] = useState(false);

    // Фильтры
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [postFilter, setPostFilter] = useState('ALL');
    const [authorFilter, setAuthorFilter] = useState('ALL');

    // Модальное окно деталей
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedComment, setSelectedComment] = useState(null);

    // Модальное окно редактирования
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingComment, setEditingComment] = useState(null);
    const [editContent, setEditContent] = useState('');

    // Пагинация
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Загрузка комментариев
    const loadComments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: currentPage - 1,
                size: pageSize
            };
            if (statusFilter !== 'ALL') {
                params.status = statusFilter;
            }
            if (postFilter !== 'ALL') {
                params.postId = postFilter;
            }

            const data = await forumApi.listComments(params);
            console.log('[AdminForumComments] Loaded comments:', data);

            let commentsList = [];
            let total = 0;

            if (Array.isArray(data)) {
                commentsList = data;
                total = data.length;
            } else if (data && Array.isArray(data.content)) {
                commentsList = data.content;
                total = data.totalElements || data.total || data.content.length;
            }

            setComments(commentsList);
            setTotalItems(total);
        } catch (err) {
            setError(`Не удалось загрузить комментарии: ${err.message}`);
            console.error('[AdminForumComments] Failed to load comments:', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, postFilter, currentPage, pageSize]);

    // Загрузка списка постов для фильтра
    const loadPosts = useCallback(async () => {
        try {
            const data = await forumApi.listPosts({ size: 200 });
            let postsList = [];
            if (Array.isArray(data)) {
                postsList = data;
            } else if (data && Array.isArray(data.content)) {
                postsList = data.content;
            }
            setPosts(postsList);
        } catch (err) {
            console.warn('[AdminForumComments] Failed to load posts for filter:', err.message);
        }
    }, []);

    useEffect(() => {
        loadComments();
    }, [loadComments]);

    useEffect(() => {
        loadPosts();
    }, [loadPosts]);

    // Фильтрация по поиску и автору
    const filteredComments = comments.filter(comment => {
        const matchesSearch = searchQuery === '' || 
            comment.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            comment.author?.username?.toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesAuthor = authorFilter === 'ALL' || 
            comment.authorId === authorFilter ||
            comment.author?.id === authorFilter;
        
        return matchesSearch && matchesAuthor;
    });

    // Получение цвета бейджа статуса
    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'PUBLISHED':
                return 'success';
            case 'HIDDEN':
                return 'warning';
            case 'DELETED':
                return 'danger';
            case 'PENDING':
            default:
                return 'secondary';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'PENDING':
                return 'На модерации';
            case 'PUBLISHED':
                return 'Опубликован';
            case 'HIDDEN':
                return 'Скрыт';
            case 'DELETED':
                return 'Удалён';
            default:
                return status;
        }
    };

    // Опубликовать комментарий
    const handlePublish = async (comment) => {
        if (!window.confirm(`Опубликовать комментарий автора "${comment.author?.username}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.updateComment(comment.id, { status: 'PUBLISHED' });
            await loadComments();
        } catch (err) {
            setError(`Не удалось опубликовать комментарий: ${err.message}`);
            console.error('[AdminForumComments] Failed to publish comment:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Скрыть комментарий
    const handleHide = async (comment) => {
        if (!window.confirm(`Скрыть комментарий автора "${comment.author?.username}"?`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.updateComment(comment.id, { status: 'HIDDEN' });
            await loadComments();
        } catch (err) {
            setError(`Не удалось скрыть комментарий: ${err.message}`);
            console.error('[AdminForumComments] Failed to hide comment:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Удалить комментарий
    const handleDelete = async (comment) => {
        if (!window.confirm(`Удалить комментарий автора "${comment.author?.username}"?\n\nЭто действие необратимо!`)) {
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.deleteComment(comment.id);
            await loadComments();
        } catch (err) {
            setError(`Не удалось удалить комментарий: ${err.message}`);
            console.error('[AdminForumComments] Failed to delete comment:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // Открытие модального окна с деталями
    const handleViewDetails = (comment) => {
        setSelectedComment(comment);
        setShowDetailModal(true);
    };

    // Открытие модального окна редактирования
    const handleEdit = (comment) => {
        setEditingComment(comment);
        setEditContent(comment.content);
        setShowEditModal(true);
    };

    // Сохранение отредактированного комментария
    const handleSaveEdit = async () => {
        if (!editContent.trim()) {
            setError('Содержимое комментария не может быть пустым');
            return;
        }

        try {
            setActionLoading(true);
            await forumApi.updateComment(editingComment.id, { content: editContent });
            await loadComments();
            setShowEditModal(false);
        } catch (err) {
            setError(`Не удалось сохранить изменения: ${err.message}`);
            console.error('[AdminForumComments] Failed to update comment:', err);
        } finally {
            setActionLoading(false);
        }
    };

    // ---- Batch-операции ----

    const isAllSelected = comments.length > 0 && comments.every(c => selectedIds.has(c.id));
    const isSomeSelected = selectedIds.size > 0;

    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(comments.map(c => c.id)));
        }
    };

    const handleToggleSelect = (commentId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(commentId)) {
                next.delete(commentId);
            } else {
                next.add(commentId);
            }
            return next;
        });
    };

    const handleBatchPublish = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Опубликовать ${selectedIds.size} комментариев?`)) return;

        setBatchLoading(true);
        setError(null);
        let successCount = 0;
        for (const id of selectedIds) {
            try {
                await forumApi.updateComment(id, { status: 'PUBLISHED' });
                successCount++;
            } catch (err) {
                console.error(`[AdminForumComments] Batch publish failed for comment ${id}:`, err.message);
            }
        }
        setSelectedIds(new Set());
        await loadComments();
        if (successCount < selectedIds.size) {
            setError(`Опубликовано ${successCount} из ${selectedIds.size} комментариев (${selectedIds.size - successCount} с ошибками)`);
        }
        setBatchLoading(false);
    };

    const handleBatchHide = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Скрыть ${selectedIds.size} комментариев?`)) return;

        setBatchLoading(true);
        setError(null);
        let successCount = 0;
        for (const id of selectedIds) {
            try {
                await forumApi.updateComment(id, { status: 'HIDDEN' });
                successCount++;
            } catch (err) {
                console.error(`[AdminForumComments] Batch hide failed for comment ${id}:`, err.message);
            }
        }
        setSelectedIds(new Set());
        await loadComments();
        if (successCount < selectedIds.size) {
            setError(`Скрыто ${successCount} из ${selectedIds.size} комментариев (${selectedIds.size - successCount} с ошибками)`);
        }
        setBatchLoading(false);
    };

    const handleBatchDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Удалить ${selectedIds.size} комментариев?\n\nЭто действие необратимо!`)) return;

        setBatchLoading(true);
        setError(null);
        let successCount = 0;
        for (const id of selectedIds) {
            try {
                await forumApi.deleteComment(id);
                successCount++;
            } catch (err) {
                console.error(`[AdminForumComments] Batch delete failed for comment ${id}:`, err.message);
            }
        }
        setSelectedIds(new Set());
        await loadComments();
        if (successCount < selectedIds.size) {
            setError(`Удалено ${successCount} из ${selectedIds.size} комментариев (${selectedIds.size - successCount} с ошибками)`);
        }
        setBatchLoading(false);
    };

    // Получение названия поста
    const getPostTitle = (comment) => {
        if (comment.post?.title) {
            return comment.post.title;
        }
        const post = posts.find(p => p.id === comment.postId);
        return post?.title || `Пост #${comment.postId}`;
    };

    // Получение уникальных авторов для фильтра
    const uniqueAuthors = [...new Map(
        comments.map(comment => [comment.authorId, comment.author])
    ).values()].filter(Boolean);

    if (loading) {
        return (
            <Container fluid className="admin-loading">
                <HrnbLoader />
            </Container>
        );
    }

    return (
        <Container fluid>
            <h1 className="admin-page-title">Модерация комментариев</h1>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            <div className="admin-pagination-header">
                <Form.Select
                    className="admin-page-size-selector"
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                    }}
                >
                    <option value={5}>5 на странице</option>
                    <option value={10}>10 на странице</option>
                    <option value={25}>25 на странице</option>
                    <option value={50}>50 на странице</option>
                </Form.Select>
            </div>

            <HrnbCard>
                <div className="admin-filters-row">
                <div className="admin-search-bar">
                    <InputGroup>
                        <InputGroup.Text>🔍</InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Поиск по контенту или автору..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </InputGroup>
                </div>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="status-filter">
                        Статус: {statusFilter === 'ALL' ? 'Все' : getStatusLabel(statusFilter)}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setStatusFilter('ALL')}>Все</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('PENDING')}>На модерации</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('PUBLISHED')}>Опубликованы</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('HIDDEN')}>Скрыты</Dropdown.Item>
                        <Dropdown.Item onClick={() => setStatusFilter('DELETED')}>Удалены</Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="post-filter">
                        Пост: {postFilter === 'ALL' ? 'Все' : posts.find(p => p.id === postFilter)?.title || 'Все'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Dropdown.Item onClick={() => setPostFilter('ALL')}>Все</Dropdown.Item>
                        {posts.map(post => (
                            <Dropdown.Item 
                                key={post.id} 
                                onClick={() => setPostFilter(post.id)}
                            >
                                {post.title}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>

                <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" id="author-filter">
                        Автор: {authorFilter === 'ALL' ? 'Все' : uniqueAuthors.find(a => a.id === authorFilter)?.username || 'Все'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <Dropdown.Item onClick={() => setAuthorFilter('ALL')}>Все</Dropdown.Item>
                        {uniqueAuthors.map(author => (
                            <Dropdown.Item 
                                key={author.id} 
                                onClick={() => setAuthorFilter(author.id)}
                            >
                                {author.username}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {isSomeSelected && (
                <div className="admin-batch-actions">
                    <span className="admin-batch-actions-count">
                        Выбрано: {selectedIds.size}
                    </span>
                    <Button
                        variant="outline-success"
                        size="sm"
                        onClick={handleBatchPublish}
                        disabled={batchLoading}
                    >
                        ✓ Опубликовать
                    </Button>
                    <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={handleBatchHide}
                        disabled={batchLoading}
                    >
                        👁️‍🗨️ Скрыть
                    </Button>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={handleBatchDelete}
                        disabled={batchLoading}
                    >
                        🗑️ Удалить
                    </Button>
                    {batchLoading && <HrnbLoader size="sm" />}
                    <Button
                        variant="link"
                        size="sm"
                        onClick={() => setSelectedIds(new Set())}
                        disabled={batchLoading}
                        className="admin-batch-clear"
                    >
                        Снять выделение
                    </Button>
                </div>
            )}

            <HrnbCard>
                <Table responsive hover className="admin-comments-table">
                    <thead>
                        <tr>
                            <th className="admin-checkbox-cell">
                                {comments.length > 0 && (
                                    <Form.Check
                                        type="checkbox"
                                        checked={isAllSelected}
                                        onChange={handleToggleSelectAll}
                                        aria-label="Выбрать все"
                                    />
                                )}
                            </th>
                            <th>ID</th>
                            <th>Контент</th>
                            <th>Автор</th>
                            <th>Пост</th>
                            <th>Статус</th>
                            <th>Лайки</th>
                            <th>Дата</th>
                            <th>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredComments.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="text-center text-muted">
                                    {searchQuery ? 'Комментарии не найдены' : 'Комментариев пока нет'}
                                </td>
                            </tr>
                        ) : (
                            filteredComments.map((comment) => (
                                <tr key={comment.id} className={selectedIds.has(comment.id) ? 'admin-comment-row-selected' : ''}>
                                    <td className="admin-checkbox-cell">
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedIds.has(comment.id)}
                                            onChange={() => handleToggleSelect(comment.id)}
                                            aria-label={`Выбрать комментарий ${comment.id}`}
                                        />
                                    </td>
                                    <td>{comment.id}</td>
                                    <td className="comment-content">
                                        {comment.content?.substring(0, 80)}
                                        {comment.content?.length > 80 ? '...' : ''}
                                    </td>
                                    <td>
                                        <strong>{comment.author?.displayName || 'Аноним'}</strong>
                                    </td>
                                    <td>
                                        <span className="post-title">
                                            {getPostTitle(comment)}
                                        </span>
                                    </td>
                                    <td>
                                        <Badge bg={getStatusBadgeVariant(comment.status)}>
                                            {comment.status}
                                        </Badge>
                                    </td>
                                    <td>{comment.likeCount || 0}</td>
                                    <td>
                                        {comment.createdAt 
                                            ? new Date(comment.createdAt).toLocaleDateString('ru-RU')
                                            : '-'}
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-info"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleViewDetails(comment)}
                                        >
                                            👁️
                                        </Button>
                                        
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleEdit(comment)}
                                        >
                                            ✏️
                                        </Button>
                                        
                                        {comment.status === 'PENDING' && (
                                            <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handlePublish(comment)}
                                                disabled={actionLoading}
                                            >
                                                ✓
                                            </Button>
                                        )}
                                        
                                        {comment.status === 'PUBLISHED' && (
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => handleHide(comment)}
                                                disabled={actionLoading}
                                            >
                                                👁️‍🗨️
                                            </Button>
                                        )}
                                        
                                        {comment.status !== 'DELETED' && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDelete(comment)}
                                                disabled={actionLoading}
                                            >
                                                🗑️
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                <AdminPagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                />
            </HrnbCard>

            {/* Модальное окно с деталями комментария */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Детали комментария</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedComment && (
                        <>
                            <div className="comment-header mb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="mb-1">
                                            {selectedComment.author?.username || 'Аноним'}
                                        </h5>
                                        <small className="text-muted">
                                            {selectedComment.createdAt 
                                                ? new Date(selectedComment.createdAt).toLocaleString('ru-RU')
                                                : '-'}
                                        </small>
                                    </div>
                                    <Badge bg={getStatusBadgeVariant(selectedComment.status)}>
                                        {getStatusLabel(selectedComment.status)}
                                    </Badge>
                                </div>
                            </div>

                            <hr />

                            <div className="comment-content-full mb-3">
                                <h6>Содержимое:</h6>
                                <p>{selectedComment.content}</p>
                            </div>

                            <hr />

                            <div className="comment-details">
                                <h6>Информация</h6>
                                <table className="table table-sm">
                                    <tbody>
                                        <tr>
                                            <td><strong>ID комментария:</strong></td>
                                            <td>{selectedComment.id}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>ID поста:</strong></td>
                                            <td>{selectedComment.postId}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Пост:</strong></td>
                                            <td>
                                                <a 
                                                    href={`/forum/posts/${selectedComment.postId}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                >
                                                    {getPostTitle(selectedComment)}
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Автор ID:</strong></td>
                                            <td>{selectedComment.authorId}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Лайки:</strong></td>
                                            <td>{selectedComment.likeCount || 0}</td>
                                        </tr>
                                        {selectedComment.parentCommentId && (
                                            <tr>
                                                <td><strong>Ответ на:</strong></td>
                                                <td>Комментарий #{selectedComment.parentCommentId}</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Модальное окно редактирования */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактирование комментария</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingComment && (
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Автор</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    value={editingComment.author?.username || 'Аноним'} 
                                    disabled 
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Пост</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    value={getPostTitle(editingComment)} 
                                    disabled 
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Содержимое *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    value={editContent}
                                    onChange={(e) => setEditContent(e.target.value)}
                                    placeholder="Введите текст комментария..."
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Отмена
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleSaveEdit}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Сохранение...' : 'Сохранить'}
                    </Button>
                </Modal.Footer>
            </Modal>
            </HrnbCard>
        </Container>
    );
};

export default AdminForumComments;
